
import React, { useState, useEffect, useRef } from 'react';
import { Button, Badge, Card } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { useLeague } from '../components/LeagueContext';
import { UserRole } from '../types';
import { API_BASE_URL } from '../config';
import { AlertTriangle, Send, Video, Clock, CheckCircle, X, MessageSquare, Shield, Eye, Trash2, RefreshCw, FileVideo, ChevronDown, ChevronUp } from 'lucide-react';

export const StewardTickets: React.FC = () => {
    const { user } = useAuth();
    const { drivers } = useLeague();
    const [tickets, setTickets] = useState<any[]>([]);
    const [events, setEvents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [expandedTicket, setExpandedTicket] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const isSteward = user?.roles?.some((r: string) => [UserRole.ADMIN, UserRole.WEBADMIN, UserRole.STEWARD].includes(r as UserRole));

    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [eventId, setEventId] = useState('');
    const [accusedId, setAccusedId] = useState('');
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    // Steward response
    const [responseText, setResponseText] = useState('');
    const [respondingTo, setRespondingTo] = useState<string | null>(null);

    const loadTickets = () => {
        setLoading(true);
        const scope = isSteward ? 'all' : 'my';
        apiRequest<any>(`steward_tickets.php?scope=${scope}&userId=${user?.id}`)
            .then(r => setTickets(Array.isArray(r) ? r : []))
            .catch(() => setTickets([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadTickets(); }, []);
    useEffect(() => { apiRequest<any[]>('events.php?scope=recent').then(r => setEvents(Array.isArray(r) ? r : [])).catch(() => []); }, []);

    const handleSubmit = async () => {
        if (!title.trim()) return;
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('reporter_id', user!.id);
            formData.append('title', title);
            formData.append('description', description);
            if (eventId) formData.append('event_id', eventId);
            if (accusedId) formData.append('accused_id', accusedId);
            if (videoFile) formData.append('video', videoFile);

            const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
            const url = `${baseUrl ? baseUrl + '/' : './'}steward_tickets.php`;
            await fetch(url, { method: 'POST', body: formData });

            setTitle(''); setDescription(''); setEventId(''); setAccusedId(''); setVideoFile(null); setShowForm(false);
            loadTickets();
        } catch (e: any) { alert('Fehler: ' + e.message); }
        finally { setSaving(false); }
    };

    const handleStatusChange = async (ticketId: string, status: string, response?: string) => {
        await apiRequest('steward_tickets.php', 'PUT', { id: ticketId, status, steward_response: response || undefined, resolved_by: user?.id });
        setRespondingTo(null); setResponseText('');
        loadTickets();
    };

    const handleDelete = async (ticketId: string) => {
        if (!confirm('Ticket endgültig löschen?')) return;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        await fetch(`${baseUrl ? baseUrl + '/' : './'}steward_tickets.php?id=${ticketId}`, { method: 'DELETE' });
        loadTickets();
    };

    const getVideoUrl = (path: string) => {
        if (!path) return '';
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return `${baseUrl ? baseUrl + '/' : './'}${path}`;
    };

    const statusColors: Record<string, string> = {
        OPEN: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
        UNDER_REVIEW: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
        RESOLVED: 'bg-green-500/10 text-green-400 border-green-500/30',
        DISMISSED: 'bg-gray-500/10 text-gray-500 border-gray-500/30',
    };
    const statusLabels: Record<string, string> = { OPEN: 'Offen', UNDER_REVIEW: 'In Prüfung', RESOLVED: 'Entschieden', DISMISSED: 'Abgelehnt' };

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-fade-in pb-20">
            {/* Header */}
            <div className="relative overflow-hidden rounded-xl border border-gray-800/40 bg-gradient-to-br from-[#12121a] via-[#15151e] to-[#12121a] p-8">
                <div className="absolute top-0 right-0 w-48 h-48 bg-red-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-1.5 h-10 bg-red-500 rounded-full" />
                        <div>
                            <h1 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter text-white leading-none">Steward Tickets</h1>
                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.3em] mt-1">Vorfälle melden · Video-Beweis · 7 Tage Aufbewahrung</p>
                        </div>
                    </div>
                    <Button onClick={() => setShowForm(!showForm)} icon={showForm ? X : Send}>{showForm ? 'Abbrechen' : 'Vorfall melden'}</Button>
                </div>
            </div>

            {/* New Ticket Form */}
            {showForm && (
                <div className="rounded-xl border border-red-500/20 bg-[#12121a] p-6 space-y-4">
                    <div className="text-[10px] text-red-400 font-bold uppercase tracking-wider">Neues Ticket erstellen</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-[9px] text-gray-600 font-bold uppercase block mb-1">Betreff *</label>
                            <input className="w-full bg-[#0e0e16] border border-gray-800/40 rounded-lg p-2 text-sm text-white outline-none focus:border-red-500" value={title} onChange={e => setTitle(e.target.value)} placeholder="z.B. Kollision T1 Runde 5"/>
                        </div>
                        <div>
                            <label className="text-[9px] text-gray-600 font-bold uppercase block mb-1">Betroffener Fahrer</label>
                            <select className="w-full bg-[#0e0e16] border border-gray-800/40 rounded-lg p-2 text-sm text-white outline-none" value={accusedId} onChange={e => setAccusedId(e.target.value)}>
                                <option value="">-- Optional --</option>
                                {drivers.filter(d => d.id !== user?.id).map(d => <option key={d.id} value={d.id}>{d.username}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-[9px] text-gray-600 font-bold uppercase block mb-1">Event</label>
                        <select className="w-full bg-[#0e0e16] border border-gray-800/40 rounded-lg p-2 text-sm text-white outline-none" value={eventId} onChange={e => setEventId(e.target.value)}>
                            <option value="">-- Optional --</option>
                            {events.map(ev => <option key={ev.id} value={ev.id}>R{ev.roundNumber}: {ev.trackLocation}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[9px] text-gray-600 font-bold uppercase block mb-1">Beschreibung</label>
                        <textarea className="w-full bg-[#0e0e16] border border-gray-800/40 rounded-lg p-2 text-sm text-white outline-none h-24 resize-none focus:border-red-500" value={description} onChange={e => setDescription(e.target.value)} placeholder="Was ist passiert? Runde, Kurve, etc."/>
                    </div>
                    <div>
                        <label className="text-[9px] text-gray-600 font-bold uppercase block mb-1">Video-Beweis (max 100MB, wird nach 7 Tagen gelöscht)</label>
                        <div className={`rounded-lg border-2 border-dashed p-4 text-center cursor-pointer transition-all ${videoFile ? 'border-green-500/30 bg-green-500/5' : 'border-gray-800/40 hover:border-red-500/30 hover:bg-red-500/5'}`} onClick={() => fileRef.current?.click()}>
                            <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={e => setVideoFile(e.target.files?.[0] || null)}/>
                            {videoFile ? (
                                <div className="flex items-center justify-center gap-2 text-green-400">
                                    <FileVideo size={16}/><span className="text-xs font-bold">{videoFile.name}</span><span className="text-[9px] text-gray-500">({(videoFile.size / 1024 / 1024).toFixed(1)} MB)</span>
                                </div>
                            ) : (
                                <div><Video size={24} className="mx-auto mb-1 text-gray-700"/><span className="text-[10px] text-gray-600">Video auswählen (MP4, WebM, MOV)</span></div>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={handleSubmit} disabled={saving || !title.trim()} className="bg-red-600 hover:bg-red-500 border-red-500">
                            {saving ? <RefreshCw size={14} className="animate-spin mr-2"/> : <Send size={14} className="mr-2"/>}
                            Ticket einreichen
                        </Button>
                    </div>
                </div>
            )}

            {/* Tickets List */}
            {loading ? <div className="py-10 text-center"><RefreshCw className="animate-spin mx-auto text-gray-600"/></div> : (
                <div className="space-y-3">
                    {tickets.length === 0 && <div className="py-10 text-center text-gray-600 text-sm">Keine Tickets vorhanden.</div>}
                    {tickets.map(t => {
                        const isExpanded = expandedTicket === t.id;
                        const isOwn = t.reporter_id === user?.id;
                        return (
                            <div key={t.id} className="rounded-xl border border-gray-800/40 bg-[#12121a] overflow-hidden">
                                <div className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-white/[0.02]" onClick={() => setExpandedTicket(isExpanded ? null : t.id)}>
                                    <AlertTriangle size={14} className={t.status === 'OPEN' ? 'text-yellow-500' : t.status === 'UNDER_REVIEW' ? 'text-blue-400' : 'text-gray-600'}/>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold text-white truncate">{t.title}</div>
                                        <div className="text-[9px] text-gray-600">von {t.reporter_name} · {t.track_location ? `R${t.round_number}: ${t.track_location}` : 'Kein Event'} · {new Date(t.created_at).toLocaleDateString('de-DE')}</div>
                                    </div>
                                    {t.accused_name && <span className="text-[9px] text-gray-500 bg-gray-800/50 px-2 py-0.5 rounded">vs {t.accused_name}</span>}
                                    <Badge className={`text-[8px] border ${statusColors[t.status]}`}>{statusLabels[t.status]}</Badge>
                                    {t.video_path && !t.video_expired && <Video size={12} className="text-purple-400"/>}
                                    {isExpanded ? <ChevronUp size={14} className="text-gray-600"/> : <ChevronDown size={14} className="text-gray-600"/>}
                                </div>

                                {isExpanded && (
                                    <div className="px-5 pb-4 border-t border-gray-800/20 pt-3 space-y-3">
                                        {t.description && <p className="text-sm text-gray-400">{t.description}</p>}
                                        
                                        {/* Video */}
                                        {t.video_path && !t.video_expired && (
                                            <div className="rounded-lg bg-black/30 border border-gray-800/40 p-3">
                                                <div className="text-[9px] text-gray-600 font-bold uppercase mb-2 flex items-center gap-1"><Video size={10}/> Video-Beweis <span className="text-yellow-500 ml-1">(läuft ab: {new Date(t.video_expires_at).toLocaleDateString('de-DE')})</span></div>
                                                <video controls className="w-full max-h-[400px] rounded" src={getVideoUrl(t.video_path)}/>
                                            </div>
                                        )}
                                        {t.video_expired && t.video_path && <div className="text-[10px] text-gray-600 italic">Video abgelaufen und gelöscht.</div>}

                                        {/* Steward Response */}
                                        {t.steward_response && (
                                            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                                                <div className="text-[9px] text-blue-400 font-bold uppercase mb-1 flex items-center gap-1"><Shield size={10}/> Steward-Entscheidung</div>
                                                <p className="text-sm text-gray-300">{t.steward_response}</p>
                                            </div>
                                        )}

                                        {/* Steward Actions */}
                                        {isSteward && (
                                            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-800/20">
                                                {t.status === 'OPEN' && <Button size="sm" onClick={() => handleStatusChange(t.id, 'UNDER_REVIEW')} className="bg-blue-600 hover:bg-blue-500 border-blue-500 text-[10px]">In Prüfung nehmen</Button>}
                                                {(t.status === 'OPEN' || t.status === 'UNDER_REVIEW') && (
                                                    <>
                                                        <Button size="sm" onClick={() => setRespondingTo(respondingTo === t.id ? null : t.id)} icon={MessageSquare} className="text-[10px]">Antworten</Button>
                                                        <Button size="sm" onClick={() => handleStatusChange(t.id, 'DISMISSED')} variant="ghost" className="text-[10px] text-gray-500">Abweisen</Button>
                                                    </>
                                                )}
                                                <Button size="sm" onClick={() => handleDelete(t.id)} variant="ghost" className="text-[10px] text-red-500 ml-auto"><Trash2 size={12}/></Button>
                                            </div>
                                        )}

                                        {/* Response form */}
                                        {respondingTo === t.id && (
                                            <div className="bg-[#0e0e16] rounded-lg p-3 border border-gray-800/40 space-y-2">
                                                <textarea className="w-full bg-[#12121a] border border-gray-800/40 rounded p-2 text-sm text-white outline-none h-20 resize-none" value={responseText} onChange={e => setResponseText(e.target.value)} placeholder="Steward-Entscheidung / Begründung..."/>
                                                <div className="flex gap-2">
                                                    <Button size="sm" onClick={() => handleStatusChange(t.id, 'RESOLVED', responseText)} className="bg-green-600 hover:bg-green-500 border-green-500 text-[10px]">Entschieden</Button>
                                                    <Button size="sm" onClick={() => handleStatusChange(t.id, 'DISMISSED', responseText)} variant="ghost" className="text-[10px]">Abweisen mit Begründung</Button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Delete own ticket if open */}
                                        {isOwn && t.status === 'OPEN' && !isSteward && (
                                            <button onClick={() => handleDelete(t.id)} className="text-[10px] text-red-500 hover:text-red-400">Ticket zurückziehen</button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
